import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useMediaQuery } from '@theme/js/hooks/swiss/useMediaQuery';
import { useAnimationContext } from '../../../context/useAnimationContext';
import { useCustomAnimationListener } from '../useCustomAnimationListener';
import { useDeviceSelector } from 'js/store/hooks';
import { pick } from 'ramda';
import { clsx } from 'js/utils/classNames.util';
import { Image } from 'js/components/common/Image';

export const useGameImageAnimation = (showGameDetails = false) => {
    const [animationName, setAnimationName] = useState<string | null>(null);
    const elementRef = useRef<HTMLDivElement>(null);
    const { shouldAnimateOnOpen } = useAnimationContext();

    const { isPortrait } = useDeviceSelector(
        pick(['isPortrait'])
    );

    const [isSmallDevice, isSmallLandscapeDevice, isMediumLandscapeDevice, largeDevice] = useMediaQuery([
        '(max-device-height: 320px) and (min-device-height: 200px)',
        '(max-height: 300px)',
        '(min-width: 700px)',
        '(min-width: 800px) and (max-height: 340px)'
    ]);
    const portraitImageHeight = isSmallDevice ? '64%' : '80%';
    const landscapeImageHeight = isSmallLandscapeDevice || isMediumLandscapeDevice || largeDevice ? '65%' : '64%';

    useCustomAnimationListener('gamesInfoPopupShowHideDetails', () => {
        if (!elementRef.current || shouldAnimateOnOpen) {
            setAnimationName(null);
            return;
        }

        if (elementRef.current && !shouldAnimateOnOpen) {
            const elementWidth = elementRef.current?.clientWidth;
            const elementHeight = elementRef.current?.clientHeight;

            const animation = `assetsOnHideDetailsAnimation_${Date.now()}`;
            const keyframes = showGameDetails ? `
                @keyframes ${animation} {
                    from {
                        width: ${elementWidth}px;
                    }
                    to {
                        width: ${!isPortrait ? '80%' : '100%'};
                        height: ${!isPortrait ? landscapeImageHeight : '244px'};
                    }
                }
            ` : `
                @keyframes ${animation} {
                    from {
                        height: ${isPortrait ? '244px' : landscapeImageHeight};
                        width:  ${!isPortrait ? '80%' : '100%'};
                    }
                    to {
                        width: ${elementWidth}px;
                        height: ${isPortrait ? elementHeight || '100%' : portraitImageHeight};
                    }
                }
            `;

            let styleElement = document.getElementById('dynamic-animation-styles') as HTMLStyleElement;

            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.type = 'text/css';
                styleElement.id = 'dynamic-animation-styles';
                document.head.appendChild(styleElement);
            }

            styleElement.textContent = keyframes;

            setAnimationName(animation);
        }
    });

    useEffect(() => {
        return () => {
            const styleElement = document.getElementById('dynamic-animation-styles');
            if (styleElement) {
                document.head.removeChild(styleElement);
            }
        };
    }, []);

    const renderGhostElement = useCallback(({
        style,
        name,
        longImg
    }: {
        style: { [key: string]: string };
        name: string;
        longImg: string;
    }) => (
        <div
            ref={elementRef}
            className={clsx([
                style.gameImageWrapper,
                style.fakeElementWrapperHeight
            ])}
            style={{
                position: 'absolute',
                top: '-200vh'
            }}
        >
            <div
                className={clsx([
                    style.assetsHolder,
                    style.invisible
                ])}
            >
            <Image
                className={clsx([
                    style.imagePortrait
                ])}
                alt={name}
                src={longImg}
                fetchpriority="high"
            />
        </div>
    </div>
    ), []);

    return {
        animationName,
        renderGhostElement
    };
};
